import { useCallback, useEffect, useRef, useState } from "react";
import type { CropRect } from "../processing/types";
import { CropFrame } from "./CropFrame";

export interface TitleSelection {
  fileName: string;
  bytes: ArrayBuffer;
  crop4x3: CropRect;
  crop3x4: CropRect;
}

export interface TitleSelections {
  primary: TitleSelection | null;
  alternative: TitleSelection | null;
}

interface GalleryFile {
  name: string;
  getFile: () => Promise<Blob>;
}

interface Props {
  onChange: (sel: TitleSelections) => void;
  // true, solange in einem der Slots ein Bild geladen, aber nicht übernommen ist.
  onBusyChange: (busy: boolean) => void;
  // Bilder aus der Galerie – ermöglichen Drag & Drop aus dem 4:3-Raster in einen Slot.
  galleryFiles?: GalleryFile[];
  disabled?: boolean;
  // Einzelbild-Modus: Ergebnisse werden heruntergeladen statt in 00_Skaliert gespeichert.
  downloadMode?: boolean;
}

// Größte Vorschau-Kante in px – etwas kleiner, damit zwei Slots nebeneinander passen.
const PREVIEW_MAX = 200;

export function TitleImagePicker({ onChange, onBusyChange, galleryFiles, disabled, downloadMode = false }: Props) {
  const [primary, setPrimary] = useState<TitleSelection | null>(null);
  const [alternative, setAlternative] = useState<TitleSelection | null>(null);
  const [primaryBusy, setPrimaryBusy] = useState(false);
  const [altBusy, setAltBusy] = useState(false);

  useEffect(() => {
    onChange({ primary, alternative });
  }, [primary, alternative, onChange]);

  useEffect(() => {
    onBusyChange(primaryBusy || altBusy);
  }, [primaryBusy, altBusy, onBusyChange]);

  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-1">Titelbild (optional)</h2>
      <p className="text-sm text-kampmeyer-neutral-500 mb-4">
        Optional: Zieh ein Bild hierher, dann wird es zusätzlich als <strong>4:3</strong> und{" "}
        <strong>3:4</strong>{" "}
        {downloadMode ? (
          "heruntergeladen. Ohne Titelbild wird nur das gewählte Bild skaliert."
        ) : (
          <>
            in{" "}
            <code className="text-xs bg-kampmeyer-neutral-100 px-1.5 py-0.5 rounded">00_Skaliert</code>{" "}
            gespeichert. Ohne Titelbild werden nur die Ordner-Bilder skaliert.
          </>
        )}{" "}
        Tipp: Du kannst ein Bild auch direkt aus dem Raster oben hierher ziehen.
      </p>

      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-1 min-w-0">
          <TitleSlot
            heading="Hauptbild"
            names={["Titelbild_4-3.jpg", "Titelbild_3-4.jpg"]}
            onChange={setPrimary}
            onBusyChange={setPrimaryBusy}
            galleryFiles={galleryFiles}
            disabled={disabled}
          />
        </div>
        <div className="flex-1 min-w-0 md:border-l md:border-kampmeyer-neutral-200 md:pl-6">
          <TitleSlot
            heading="Alternative (optional)"
            names={["Titelbild_Alternativ_4-3.jpg", "Titelbild_Alternativ_3-4.jpg"]}
            onChange={setAlternative}
            onBusyChange={setAltBusy}
            galleryFiles={galleryFiles}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}

interface SlotProps {
  heading: string;
  names: [string, string];
  onChange: (sel: TitleSelection | null) => void;
  onBusyChange: (busy: boolean) => void;
  galleryFiles?: GalleryFile[];
  disabled?: boolean;
}

function TitleSlot({ heading, names, onChange, onBusyChange, galleryFiles, disabled }: SlotProps) {
  const [bitmap, setBitmap] = useState<ImageBitmap | null>(null);
  const [fileName, setFileName] = useState("");
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  const [crop4x3, setCrop4x3] = useState<CropRect | null>(null);
  const [crop3x4, setCrop3x4] = useState<CropRect | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleCrop4x3 = useCallback((c: CropRect) => setCrop4x3(c), []);
  const handleCrop3x4 = useCallback((c: CropRect) => setCrop3x4(c), []);

  useEffect(() => {
    if (bitmap && bytes && fileName && crop4x3 && crop3x4 && confirmed) {
      onChange({ fileName, bytes, crop4x3, crop3x4 });
    } else {
      onChange(null);
    }
    onBusyChange(!!bitmap && !confirmed);
  }, [bitmap, bytes, fileName, crop4x3, crop3x4, confirmed, onChange, onBusyChange]);

  const loadFromBuffer = async (name: string, buffer: ArrayBuffer) => {
    setError(null);
    try {
      // Gleicher Decode-Weg wie in der Pipeline (createImageBitmap ohne Optionen),
      // damit die Vorschau exakt dem späteren Zuschnitt entspricht.
      const bmp = await createImageBitmap(new Blob([buffer.slice(0)]));
      setBitmap(bmp);
      setBytes(buffer);
      setFileName(name);
      setCrop4x3(null);
      setCrop3x4(null);
      setConfirmed(false);
    } catch {
      setError(
        "Dieses Bild konnte nicht geladen werden. Bitte ein JPEG oder PNG verwenden (HEIC wird für das Titelbild nicht unterstützt).",
      );
    }
  };

  const loadFile = async (file: File) => {
    await loadFromBuffer(file.name, await file.arrayBuffer());
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    // dataTransfer synchron auslesen (nach einem await ist es nicht mehr gültig).
    const osFile = e.dataTransfer.files?.[0];
    const galleryName = e.dataTransfer.getData("text/x-skalierung-name");
    if (osFile) {
      void loadFile(osFile);
      return;
    }
    if (galleryName && galleryFiles) {
      const gf = galleryFiles.find((f) => f.name === galleryName);
      if (gf) {
        void (async () => {
          const file = await gf.getFile();
          await loadFromBuffer(gf.name, await file.arrayBuffer());
        })();
      }
    }
  };

  const clear = () => {
    setBitmap(null);
    setBytes(null);
    setFileName("");
    setCrop4x3(null);
    setCrop3x4(null);
    setConfirmed(false);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-2 mb-1">
        <h3 className="text-sm font-semibold">{heading}</h3>
        {bitmap && (
          <button
            onClick={clear}
            disabled={disabled}
            className="text-xs text-kampmeyer-neutral-500 hover:text-kampmeyer-neutral-800 underline shrink-0"
          >
            Entfernen
          </button>
        )}
      </div>
      <p className="text-xs text-kampmeyer-neutral-400 mb-3">
        <code>{names[0]}</code>, <code>{names[1]}</code>
      </p>

      {!bitmap ? (
        <>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              if (!disabled) setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => !disabled && inputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg py-10 px-4 text-center cursor-pointer transition ${
              dragOver
                ? "border-kampmeyer-primary bg-kampmeyer-primary/5"
                : "border-kampmeyer-neutral-300 hover:border-kampmeyer-neutral-400"
            } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            <p className="text-sm text-kampmeyer-neutral-600 font-medium">Bild hierher ziehen</p>
            <p className="text-xs text-kampmeyer-neutral-500 mt-1">
              oder klicken zum Auswählen (JPEG / PNG)
            </p>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void loadFile(file);
            }}
          />
        </>
      ) : (
        <>
          <p className="text-sm text-kampmeyer-neutral-500 mb-3">
            {confirmed ? (
              <>Ausschnitt übernommen. Zum Anpassen auf „Ändern“ klicken.</>
            ) : (
              <>
                Ausschnitt anpassen: mit der Maus <strong>verschieben</strong>, per Regler{" "}
                <strong>zoomen</strong>.
              </>
            )}
          </p>
          <div className="flex flex-wrap gap-4">
            <CropFrame
              bitmap={bitmap}
              aspect={4 / 3}
              label="4:3 (quer)"
              boxW={PREVIEW_MAX}
              boxH={Math.round((PREVIEW_MAX * 3) / 4)}
              onCrop={handleCrop4x3}
              disabled={disabled || confirmed}
            />
            <CropFrame
              bitmap={bitmap}
              aspect={3 / 4}
              label="3:4 (hoch)"
              boxW={Math.round((PREVIEW_MAX * 3) / 4)}
              boxH={PREVIEW_MAX}
              onCrop={handleCrop3x4}
              disabled={disabled || confirmed}
            />
          </div>

          {confirmed ? (
            <div className="mt-4 flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-green-700">✓ Übernommen (4:3 &amp; 3:4)</span>
              <button
                onClick={() => setConfirmed(false)}
                className="btn-secondary"
                disabled={disabled}
              >
                Ändern
              </button>
            </div>
          ) : (
            <div className="mt-4">
              <button
                onClick={() => setConfirmed(true)}
                className="btn-primary"
                disabled={disabled || !crop4x3 || !crop3x4}
              >
                Ausschnitt übernehmen
              </button>
            </div>
          )}

          <p className="text-xs text-kampmeyer-neutral-400 mt-3 truncate">Quelle: {fileName}</p>
        </>
      )}

      {error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
          {error}
        </div>
      )}
    </div>
  );
}

